import React, { ReactNode } from 'react';
import { cloneDeep, isEmpty, isEqual } from 'lodash';
import dayjs from 'dayjs';

import Row from 'components/common/Row';
import ModalContainer from '../../common/ModalContainer';
import Text from 'components/common/Text';
import Column from 'components/common/Column';
import { ICON_CLOSE_32 } from 'assets/icons';
import OptionTermDateV2 from './OptionTermDateV2';
import OptionRecruitPeopleV2 from './OptionRecruitPeopleV2';
import OptionDueDateFormV2 from './OptionDueDateFormV2';
import {
  ProductParamInput,
  ScheduleParam,
} from 'containers/ProductRegist/hooks/types';
import { useDateAdditionalHooks } from '../hooks/useDateAdditionalHooks';
import { InventoryTargetType, ScheduleState } from 'types/api';
import { OptionSheetType } from '../hooks/useCalenderHooks';
import AuthButton from 'components/Button/AuthButton';
import ConfirmDialog from 'components/Modal/ConfirmDialog';
import ScheduleStatusButton from './ScheduleStatusButton';
import OptionSelectFormV2 from './OptionSelectFormV2';

interface Props {
  isMobile: boolean;
  productParamInput: ProductParamInput;
  scheduleItem: ScheduleParam;
  checkedDate: Date[];
  optionSheetType: OptionSheetType;
  isDetail?: boolean;
  onClose: () => void;
  handleSetScheduleItem: (changes: Record<string, any>) => void;
  setCheckedDate: React.Dispatch<React.SetStateAction<any[]>>;
  handleOnChangeProductParamInput: (key: string, value: any) => void;
  handleClearCalenderData: (isClear: boolean) => void;
  handleChangeScheduleItem: (key: string, value: any) => void;
  handleInitScheduleItem: () => void;
}

const DateAdditionalModal = (props: Props) => {
  const {
    isMobile,
    productParamInput,
    scheduleItem,
    checkedDate,
    optionSheetType,
    isDetail,
    onClose,
    handleSetScheduleItem,
    setCheckedDate,
    handleOnChangeProductParamInput,
    handleClearCalenderData,
    handleChangeScheduleItem,
    handleInitScheduleItem,
  } = props;

  const {
    isAppliedAllItems,
    isOverDay,
    selectedItems,
    termDate,
    endDateFormat,
    endDueDate,
    isOpenDeleteDialog,
    setTermDate,
    setIsOpenDeleteDialog,
    handleSetIsAppliedAllItems,
    handleAddOptions,
    handleAddAllOptions,
    handleSetOverDay,
    handleSetEndDueDate,
    handleSetEndDateFormat,
  } = useDateAdditionalHooks(scheduleItem, handleSetScheduleItem);

  const checkOptions = isAppliedAllItems
    ? true
    : !isEmpty(scheduleItem.itemParamIds);
  const isAbled =
    checkOptions && termDate.startDate !== '' && termDate.endDate !== '';

  const handleValidCheck = () => {
    if (
      productParamInput.inventoryTargetType !== InventoryTargetType.BY_ITEM &&
      scheduleItem.minimumQuota > scheduleItem.quota
    ) {
      window.showToast('최대 인원은 최소 인원보다 적을 수 없습니다.', 'failed');

      return;
    }

    if (
      productParamInput.inventoryTargetType !== InventoryTargetType.BY_ITEM &&
      (scheduleItem.minimumQuota === 0 || scheduleItem.quota === 0)
    ) {
      window.showToast('최소/최대인원은 0명으로 설정할 수 없어요.', 'failed');

      return;
    }
  };

  const handleCreateScheduleItems = () => {
    handleValidCheck();

    let schedulItems: ScheduleParam[] = productParamInput.frip
      ? productParamInput.frip.schedules
      : [];
    let checkDuplicateSchedule: boolean = false;
    let existPrevDate: boolean = false;
    const date = new Date().getTime();

    checkedDate.forEach(item => {
      const startedTime = termDate.startDate.split(':');
      const endedTime = termDate.endDate.split(':');
      const startDate = new Date(item);
      const endDate = new Date(item);

      startDate.setHours(Number(startedTime[0]), Number(startedTime[1]), 0, 0);
      endDate.setHours(Number(endedTime[0]), Number(endedTime[1]), 0, 0);

      if (isOverDay) {
        endDate.setDate(endDate.getDate() + Number(termDate.durationDate));
      }

      let applicationEndDate = cloneDeep(startDate);

      if (endDateFormat === '1day') {
        applicationEndDate.setDate(applicationEndDate.getDate() - 1);
      }

      if (endDateFormat === '2day') {
        applicationEndDate.setDate(applicationEndDate.getDate() - 2);
      }

      if (endDateFormat === '3day') {
        applicationEndDate.setDate(applicationEndDate.getDate() - 3);
      }

      if (endDateFormat === 'select') {
        if (endDueDate.date !== 0) {
          applicationEndDate.setDate(
            applicationEndDate.getDate() - endDueDate.date,
          );
        }

        if (endDueDate.time !== 0) {
          applicationEndDate.setHours(
            applicationEndDate.getHours() - endDueDate.time,
          );
        }
      }
      let applicationStartDate = cloneDeep(applicationEndDate);
      applicationStartDate.setMonth(applicationStartDate.getMonth() - 3);

      if (startDate.getTime() - applicationEndDate.getTime() < 60 * 60 * 1000) {
        window.showToast(
          '신청 마감은 일정 시작 최소 1시간 전이어야 해요.',
          'failed',
        );

        existPrevDate = true;

        return;
      }

      if (date > applicationEndDate.getTime()) {
        window.showToast(
          '과거 날짜가 있어요. 신청 가능 기간을 미래의 날짜로 다시 선택해주세요.',
          'failed',
        );

        existPrevDate = true;

        return;
      }

      const result: ScheduleParam = {
        ...scheduleItem,
        id: Math.random().toString(),
        appliedAllItems: isAppliedAllItems,
        planParamId: null,
        term: {
          duration: dayjs(endDate).diff(startDate),
          startedAt: startDate.getTime(),
        },
        title: dayjs(startDate).format('YYYY년 M월 D일 HH:mm'),
        saleTerm: {
          startedAt: applicationStartDate.getTime(),
          endedAt: applicationEndDate.getTime(),
        },
        status: ScheduleState.EDITING,
        itemParamIds: scheduleItem.appliedAllItems
          ? []
          : Array.from(new Set(selectedItems.map(item => item.paramId))),
      };

      const hasDuplicate = schedulItems
        .filter(
          item =>
            !item.status ||
            [ScheduleState.EDITING, ScheduleState.OPENED].includes(item.status),
        )
        .some(item => item.term.startedAt === startDate.getTime());

      if (hasDuplicate) {
        checkDuplicateSchedule = true;

        return;
      }

      if (!checkDuplicateSchedule) {
        schedulItems = [...schedulItems, result];

        existPrevDate = false;
      }
    });

    if (existPrevDate) {
      return;
    }

    if (checkDuplicateSchedule) {
      window.showToast('이미 존재하는 일정이에요.', 'failed');

      return;
    }

    handleOnChangeProductParamInput('frip', {
      ...productParamInput.frip,
      schedules: schedulItems,
    });

    setCheckedDate([]);
    handleClearCalenderData(true);
    handleInitScheduleItem();
    window.showToast('일정이 추가를 완료했어요.', 'success');
    onClose();
  };

  const handleUpdateScheduleItems = () => {
    handleValidCheck();

    const updatedItems = productParamInput.frip!.schedules.map(item => item);
    const index = productParamInput.frip!.schedules.findIndex(
      item => item.id === scheduleItem.id,
    );
    const oneDayMs = 24 * 60 * 60 * 1000;

    const startedTime = termDate.startDate.split(':');
    const endedTime = termDate.endDate.split(':');
    const startDate = new Date(updatedItems[index].term.startedAt);
    const endDate = new Date(
      startDate.getTime() + oneDayMs * Number(termDate.durationDate),
    );

    startDate.setHours(Number(startedTime[0]), Number(startedTime[1]), 0, 0);
    endDate.setHours(Number(endedTime[0]), Number(endedTime[1]), 0, 0);

    if (isOverDay) {
      endDate.setTime(
        startDate.getTime() + oneDayMs * Number(termDate.durationDate),
      );
    }

    let applicationEndDate = cloneDeep(startDate);

    if (endDateFormat === '1day') {
      applicationEndDate.setDate(applicationEndDate.getDate() - 1);
    }

    if (endDateFormat === '2day') {
      applicationEndDate.setDate(applicationEndDate.getDate() - 2);
    }

    if (endDateFormat === '3day') {
      applicationEndDate.setDate(applicationEndDate.getDate() - 3);
    }

    if (endDateFormat === 'select') {
      if (endDueDate.date !== 0) {
        applicationEndDate.setDate(
          applicationEndDate.getDate() - endDueDate.date,
        );
      }

      if (endDueDate.time !== 0) {
        applicationEndDate.setHours(
          applicationEndDate.getHours() - endDueDate.time,
        );
      }
    }

    let applicationStartDate = cloneDeep(applicationEndDate);
    applicationStartDate.setMonth(applicationStartDate.getMonth() - 3);

    if (startDate.getTime() - applicationEndDate.getTime() < 60 * 60 * 1000) {
      window.showToast(
        '신청 마감은 일정 시작 최소 1시간 전이어야 해요.',
        'failed',
      );

      return;
    }

    updatedItems[index] = {
      ...updatedItems[index],
      appliedAllItems: isAppliedAllItems,
      term: {
        duration: dayjs(endDate).diff(startDate),
        startedAt: startDate.getTime(),
      },
      title: dayjs(startDate).format('YYYY년 M월 D일 HH:mm'),
      saleTerm: {
        startedAt: applicationStartDate.getTime(),
        endedAt: applicationEndDate.getTime(),
      },
      minimumQuota: Number(scheduleItem.minimumQuota),
      quota: scheduleItem.quota,
      itemParamIds: scheduleItem.appliedAllItems
        ? []
        : Array.from(new Set(selectedItems.map(item => item.paramId))).filter(
            item => item !== undefined,
          ),
    };

    const isDuplicate = productParamInput.frip.schedules
      .filter(
        schedule =>
          !schedule.status ||
          [ScheduleState.EDITING, ScheduleState.OPENED].includes(
            schedule.status,
          ),
      )
      .some(
        schedule =>
          schedule.term.startedAt === startDate.getTime() &&
          schedule.paramId !== updatedItems[index].paramId,
      );

    if (isDuplicate) {
      window.showToast('이미 존재하는 일정이에요.', 'failed');

      return;
    }

    handleOnChangeProductParamInput('frip', {
      ...productParamInput.frip,
      schedules: updatedItems,
    });

    setCheckedDate([]);
    onClose();
  };

  const handleDeleteScheduleItems = () => {
    handleOnChangeProductParamInput('frip', {
      ...productParamInput.frip,
      schedules: productParamInput.frip?.schedules.filter(
        item => item.id !== scheduleItem.id,
      ),
    });

    setCheckedDate([]);
    setIsOpenDeleteDialog(false);
    window.showToast('일정이 수정을 완료했어요.', 'success');
    onClose();
  };

  const renderHeader = (): JSX.Element => {
    return (
      <Row
        justifyContent="space-between"
        style={{
          position: 'sticky',
          top: '0',
          padding: isMobile ? '24px 20px' : '32px 20px ',
          width: '100%',
          background: '#fff',
          zIndex: 8,
        }}
      >
        <Row gap="12px">
          {isDetail && scheduleItem.status && (
            <ScheduleStatusButton
              status={scheduleItem.status}
              isMobile={isMobile}
            />
          )}
          <Text color="#1b1c1e" size={isMobile ? '20px' : '24px'}>
            {optionSheetType === OptionSheetType.UPDATE
              ? `${dayjs(scheduleItem.term.startedAt).format(
                  'YY년 M월 D일 HH:mm',
                )}
              ~
             ${dayjs(
               scheduleItem.term.startedAt + scheduleItem.term.duration,
             ).format('HH:mm')}`
              : isMobile
                ? '일정 추가'
                : '선택한 날짜에 일정 추가'}
          </Text>
        </Row>
        <Row onClick={onClose}>
          <ICON_CLOSE_32
            color="#70737C"
            style={{
              width: isMobile ? '24px' : 'auto',
              height: isMobile ? '24px' : 'auto',
            }}
          />
        </Row>
      </Row>
    );
  };

  const renderContents = (): JSX.Element => {
    return (
      <Column
        align="flex-start"
        gap="32px"
        style={{ padding: isMobile ? '16px 20px 40px' : '12px 20px 40px' }}
      >
        <OptionSelectFormV2
          isMobile={isMobile}
          isAppliedAllItems={isAppliedAllItems}
          isDisable={false}
          handleSetIsAppliedAllItems={handleSetIsAppliedAllItems}
          handleAddOptions={handleAddOptions}
          productParamInput={productParamInput}
          scheduleItem={scheduleItem}
          handleAddAllOptions={handleAddAllOptions}
        />
        <OptionTermDateV2
          isMobile={isMobile}
          isOverDay={isOverDay}
          termDate={termDate}
          isDisable={false}
          setTermDate={setTermDate}
          handleSetOverDay={handleSetOverDay}
        />
        {!(
          productParamInput.frip.attachedToSchedule &&
          productParamInput.inventoryTargetType === InventoryTargetType.BY_ITEM
        ) && (
          <OptionRecruitPeopleV2
            scheduleItem={scheduleItem}
            isDisable={scheduleItem.status === ScheduleState.CANCELED}
            handleSetScheduleItem={handleSetScheduleItem}
          />
        )}
        <OptionDueDateFormV2
          scheduleItem={scheduleItem}
          endDateFormat={endDateFormat}
          endDueDate={endDueDate}
          isDisable={false}
          handleSetEndDueDate={handleSetEndDueDate}
          handleSetEndDateFormat={handleSetEndDateFormat}
        />
      </Column>
    );
  };

  const renderBottomSheet = (): JSX.Element => {
    return (
      <Row
        justifyContent="flex-end"
        gap="16px"
        style={{
          position: 'sticky',
          bottom: '0',
          padding: '16px 20px 32px',
          width: '100%',
          background: '#fff',
          borderTop: '1px solid #Eaebec',
        }}
      >
        {optionSheetType === OptionSheetType.CREATE ? (
          <AuthButton
            name="완료"
            isAbled={isAbled}
            onClick={handleCreateScheduleItems}
            style={{
              padding: '12px 24px',
              width: 'auto',
              minWidth: '76px',
              borderRadius: '12px',
            }}
          />
        ) : (
          <>
            <AuthButton
              name="일정 삭제하기"
              isAbled={true}
              onClick={() => setIsOpenDeleteDialog(true)}
              style={{
                padding: '12px 24px',
                width: 'auto',
                minWidth: '76px',
                borderRadius: '12px',
                background: '#FFF0F0',
                color: '#FF4242',
              }}
            />
            <AuthButton
              name="수정"
              isAbled={isAbled}
              onClick={handleUpdateScheduleItems}
              style={{
                padding: '12px 24px',
                width: 'auto',
                minWidth: '76px',
                borderRadius: '12px',
              }}
            />
          </>
        )}
      </Row>
    );
  };

  return (
    <>
      <ModalContainer
        isMobile={isMobile}
        isAbled={true}
        isBottomSheet
        onCloseText=" "
        onAcceptText="완료"
        onClose={() => {}}
        onClick={() => {}}
        style={{ gap: '0' }}
      >
        {renderHeader()}
        {renderContents()}
        {renderBottomSheet()}
      </ModalContainer>
      {isOpenDeleteDialog && (
        <ConfirmDialog
          title="선택한 일정을 삭제할까요?"
          description="일정은 저장되지 않고 삭제됩니다."
          onCloseText="아니요"
          onClose={() => {
            setIsOpenDeleteDialog(false);
          }}
          onAcceptText="네, 삭제할게요"
          onAccept={handleDeleteScheduleItems}
        />
      )}
    </>
  );
};

export default DateAdditionalModal;
